import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Copy, ShareNetwork, EnvelopeSimple, SignOut, CaretDown, CaretRight, UserPlus, Key, DotsThreeVertical, User } from "@phosphor-icons/react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { RoleBadge } from "@/components/RoleBadge";

export function FamilyManager() {
  const { t } = useApp();
  const [family, setFamily] = useState(null);
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState("family");
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [joinCode, setJoinCode] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [menuFor, setMenuFor] = useState(null);

  const load = useCallback(async () => {
    const fam = (await api.get("/family")).data;
    setFamily(fam);
    try { setGroups((await api.get("/groups/mine")).data || []); } catch { setGroups([]); }
  }, []);

  useEffect(() => {
    const pending = localStorage.getItem("sb_pending_family");
    (async () => {
      if (pending) {
        try { await api.post("/family/join", { code: pending }); toast.success(t("joined")); } catch { /* ignore */ }
        localStorage.removeItem("sb_pending_family");
      }
      load();
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    const h = () => load();
    window.addEventListener("sb:people-changed", h);
    window.addEventListener("focus", h);
    return () => { window.removeEventListener("sb:people-changed", h); window.removeEventListener("focus", h); };
  }, [load]);

  if (!family) return null;

  const contexts = [{ key: "family", type: "family", label: t("familyTitle") }, ...groups.map((g) => ({ key: g.id, type: "group", label: g.name, g }))];
  const current = contexts.find((c) => c.key === sel) || contexts[0];
  const isFamily = current.type === "family";
  const g = current.g;

  const code = isFamily ? family.invite_code : g?.join_code;
  const canManage = isFamily ? family.is_owner : !!g?.is_leader;
  const canInvite = isFamily ? true : !!g?.is_leader;
  const myRole = isFamily ? (family.is_owner ? "owner" : family.my_role) : (g?.is_leader ? "leader" : "member");
  const members = isFamily ? family.members : (g?.members || []);
  const link = code ? (isFamily ? `${window.location.origin}/family/join?code=${code}` : `${window.location.origin}/groups/join?code=${code}`) : "";
  const canLeave = !(current.type === "group" && g?.is_leader);

  const invite = async () => {
    if (!email.trim()) return;
    try {
      if (isFamily) await api.post("/family/invite", { email, role: inviteRole });
      else await api.post(`/groups/${g.id}/invite`, { email });
      setEmail(""); toast.success(t("inviteSent")); window.dispatchEvent(new Event("sb:people-changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const join = async () => {
    const c = joinCode.trim(); if (!c) return;
    try {
      try { await api.post("/groups/join", { code: c }); }
      catch (e1) { if (e1?.response?.status === 404) await api.post("/family/join", { code: c }); else throw e1; }
      setJoinCode(""); toast.success(t("joined")); await load(); window.dispatchEvent(new Event("sb:people-changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const leave = async () => {
    try {
      if (isFamily) await api.post("/family/leave");
      else await api.post(`/groups/${g.id}/leave`);
      toast.success(t("left")); setSel("family"); await load(); window.dispatchEvent(new Event("sb:people-changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const setRole = async (uid, role) => {
    try { await api.post("/family/role", { user_id: uid, role }); toast.success(t("roleUpdated")); await load(); window.dispatchEvent(new Event("sb:people-changed")); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const removeMember = async (uid) => {
    try {
      if (isFamily) await api.post("/family/remove", { user_id: uid });
      else await api.delete(`/groups/${g.id}/members/${uid}`);
      setMenuFor(null); toast.success(t("memberRemoved")); await load(); window.dispatchEvent(new Event("sb:people-changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const secHead = "text-xs font-body uppercase tracking-wider text-gray-400";

  return (
    <div className="space-y-4" data-testid="family-card">
      {/* 1. Group selector */}
      <div className="sb-card p-4" data-testid="group-select-card">
        <label className={secHead}>Gruppe wählen</label>
        <div className="relative mt-1.5">
          <select data-testid="group-switcher" value={current.key} onChange={(e) => { setSel(e.target.value); setMenuFor(null); }}
            className="sb-input w-full !py-3 appearance-none pr-9 font-heading font-semibold text-base">
            {contexts.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <CaretDown size={18} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="text-sm font-body text-gray-500">{members.length} {members.length === 1 ? "Mitglied" : "Mitglieder"} · Deine Rolle:</span>
          <RoleBadge role={myRole} />
        </div>
      </div>

      {/* 2. Members */}
      <div className="sb-card p-4" data-testid="members-section">
        <div className="flex items-center gap-2 mb-3">
          <Users size={18} weight="duotone" style={{ color: "var(--app-strong)" }} />
          <h3 className="font-heading font-semibold">Mitglieder</h3>
          <span className="font-body text-xs text-gray-400">· {members.length}</span>
        </div>
        <div className="space-y-2" data-testid="family-members">
          {members.length === 0 && <p className="font-body text-sm text-gray-400">Noch keine Mitglieder.</p>}
          {members.map((m) => {
            const isOwnerRow = isFamily ? m.is_owner : m.role === "leader";
            const badgeRole = isFamily ? (m.is_owner ? "owner" : m.role) : m.role;
            const canRow = canManage && !isOwnerRow && !m.is_you;
            const open = menuFor === m.user_id;
            return (
              <div key={m.user_id} data-testid={`fam-member-${m.user_id}`} className="border-2 border-[var(--app-border)] rounded-2xl px-3 py-2.5 bg-[var(--app-bg)]">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[var(--app-surface)] border-2 border-[var(--app-border)] flex items-center justify-center shrink-0">
                      <User size={18} weight="duotone" style={{ color: "var(--app-strong)" }} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading text-sm truncate">{m.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {m.is_you && <span className="text-[11px] font-body text-gray-400">{t("you")} ·</span>}
                        <RoleBadge role={badgeRole} />
                      </div>
                    </div>
                  </div>
                  {canRow && (
                    <button data-testid={`fam-member-menu-${m.user_id}`} onClick={() => setMenuFor(open ? null : m.user_id)} aria-label="Aktionen"
                      className="w-8 h-8 rounded-xl border-2 border-[var(--app-border)] bg-[var(--app-surface)] flex items-center justify-center shrink-0 active:scale-90 transition-transform">
                      <DotsThreeVertical size={18} weight="bold" />
                    </button>
                  )}
                </div>
                {canRow && open && (
                  <div className="mt-2.5 pt-2.5 border-t-2 border-[var(--app-border)] space-y-2">
                    {isFamily && (
                      <div>
                        <label className={secHead}>{t("inviteRoleLabel")}</label>
                        <select data-testid={`family-role-${m.user_id}`} value={m.role} onChange={(e) => setRole(m.user_id, e.target.value)} className="sb-input w-full mt-1 !py-2 text-sm">
                          <option value="editor">{t("roleEditor")}</option>
                          <option value="viewer">{t("roleViewer")}</option>
                        </select>
                      </div>
                    )}
                    <button data-testid={`family-remove-${m.user_id}`} onClick={() => removeMember(m.user_id)}
                      className="sb-btn w-full py-2 bg-[var(--app-surface)] text-red-500 text-sm font-medium">{t("removeMember")}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Invite member (collapsible) */}
      {canInvite && (
        <div className="sb-card p-4" data-testid="invite-section">
          <button data-testid="invite-toggle" onClick={() => setShowInvite((v) => !v)} className="w-full flex items-center gap-2 text-left">
            <UserPlus size={18} weight="duotone" style={{ color: "var(--app-strong)" }} />
            <span className="font-heading font-semibold flex-1">Mitglied einladen</span>
            {showInvite ? <CaretDown size={18} weight="bold" className="text-gray-500" /> : <CaretRight size={18} weight="bold" className="text-gray-500" />}
          </button>
          {showInvite && (
            <div className="mt-3 space-y-3">
              {code && (
                <div>
                  <p className={secHead + " mb-1"}>{t("inviteCode")}</p>
                  <div className="flex gap-2">
                    <div className="sb-input flex-1 font-mono tracking-widest text-base flex items-center" data-testid="family-invite-code">{code}</div>
                    <button data-testid="family-copy-code" onClick={() => { copyText(code); toast.success(t("codeCopied")); }} className="sb-btn px-3 bg-[var(--app-surface)]" aria-label="Code kopieren"><Copy size={18} weight="bold" /></button>
                    <button data-testid="family-copy-link" onClick={() => { copyText(link); toast.success(t("codeCopied")); }} className="sb-btn px-3 bg-[var(--app-surface)]" aria-label="Einladung teilen"><ShareNetwork size={18} weight="bold" /></button>
                  </div>
                </div>
              )}
              <div>
                <p className={secHead + " mb-1"}>oder per E-Mail</p>
                <div className="flex gap-2">
                  <input data-testid="family-invite-email" className="sb-input flex-1" placeholder={t("inviteEmailPlaceholder")} value={email} onChange={(e) => setEmail(e.target.value)} />
                  <button data-testid="family-invite-send" onClick={invite} className="sb-btn px-4 text-white flex items-center gap-1" style={{ background: "var(--app-primary)" }}><EnvelopeSimple size={18} weight="bold" /></button>
                </div>
                {isFamily && (
                  <div className="mt-2">
                    <label className={secHead}>{t("inviteRoleLabel")}</label>
                    <select data-testid="family-invite-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="sb-input w-full mt-1 !py-2 text-sm">
                      <option value="editor">{t("roleEditor")}</option>
                      <option value="viewer">{t("roleViewer")}</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. Join a group (collapsible) */}
      <div className="sb-card p-4" data-testid="join-section">
        <button data-testid="join-toggle" onClick={() => setShowJoin((v) => !v)} className="w-full flex items-center gap-2 text-left">
          <Key size={18} weight="duotone" style={{ color: "var(--app-strong)" }} />
          <span className="font-heading font-semibold flex-1">Einer Gruppe beitreten</span>
          {showJoin ? <CaretDown size={18} weight="bold" className="text-gray-500" /> : <CaretRight size={18} weight="bold" className="text-gray-500" />}
        </button>
        {showJoin && (
          <div className="mt-3">
            <p className="font-body text-sm text-gray-500 mb-1.5">Du hast bereits einen Einladungscode?</p>
            <div className="flex gap-2">
              <input data-testid="family-join-code" className="sb-input flex-1" placeholder={t("joinCodePlaceholder")} value={joinCode} onChange={(e) => setJoinCode(e.target.value)} />
              <button data-testid="family-join-btn" onClick={join} className="sb-btn px-4 bg-[var(--app-surface)] font-medium">{t("join")}</button>
            </div>
          </div>
        )}
      </div>

      {/* 5. Leave (subtle) */}
      {canLeave && (
        <button data-testid="family-leave" onClick={leave} className="w-full py-2.5 text-red-500/90 font-body text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <SignOut size={16} weight="bold" />{isFamily ? t("leaveFamily") : "Gruppe verlassen"}
        </button>
      )}
    </div>
  );
}
